/**
 * Base 2D point + ring math for the floor-plan model.
 */
import polygonClipping, { Ring as PcRing } from 'polygon-clipping';
import { Point } from '../schemas';

const EPS = 1e-7;
/** Are two points coincident within geometric tolerance? */
export function samePoint(a: Point, b: Point): boolean {
  return Math.abs(a.x - b.x) < EPS && Math.abs(a.y - b.y) < EPS;
}

export function ringSignedArea(ring: readonly Point[]): number {
  const n = ring.length;
  if (n < 3) return 0;
  let sum = 0;
  for (let i = 0; i < n; i++) {
    const a = ring[i];
    const b = ring[(i + 1) % n];
    sum += a.x * b.y - b.x * a.y;
  }
  return sum / 2;
}

/** Area of a multipolygon: outer rings minus their holes. */
export const multiPolygonArea = (polygons: readonly Point[][][]): number => {
  let area = 0;
  for (const poly of polygons) {
    poly.forEach((ring, i) => {
      const a = Math.abs(ringSignedArea(ring));
      area += i === 0 ? a : -a;
    });
  }
  return area;
};

export function toPcRing(ring: Point[]): PcRing {
  const out: PcRing = ring.map(p => [p.x, p.y] as [number, number]);
  if (out.length > 0) out.push(out[0]); // closed
  return out;
}

/** A polygon-clipping ring (closed: last point repeats the first) back to an open point ring. */
export function fromPcRing(ring: PcRing): Point[] {
  const points = ring.map(([x, y]) => ({ x, y }));
  if (points.length > 1 && samePoint(points[0], points[points.length - 1])) points.pop();
  return points;
}

/**
 * `subject` (one polygon: outer ring first, holes after) minus every polygon
 * in `clips`, as polygons with holes. Clips that touch the subject's edge
 * become notches, clips that cross it split the subject - every case a
 * cutout in a wall face can produce, handled uniformly.
 */
export function subtractPolygons(subject: Point[][], clips: readonly Point[][][]): Point[][][] {
  if (!clips.length) return [subject];
  const result = polygonClipping.difference(subject.map(toPcRing), ...clips.map(rings => rings.map(toPcRing)));
  return result.map(polygon => polygon.map(fromPcRing));
}

/**
 * A convex ring shrunk inward by `distance`: every edge line moves toward the
 * interior and the half-planes are intersected, so corners (chamfers
 * included) are followed with mitered joins. An edge the inset consumes just
 * drops out, and a ring narrower than twice the distance collapses to [].
 * Convex rings only - one half-plane per edge is exact for those.
 */
export function insetConvexRing(ring: readonly Point[], distance: number): Point[] {
  if (ring.length < 3) return [];
  const inward = ringSignedArea(ring) > 0 ? 1 : -1;
  let out: Point[] = [...ring];
  for (let i = 0; i < ring.length && out.length >= 3; i++) {
    const a = ring[i], b = ring[(i + 1) % ring.length];
    const length = Math.hypot(b.x - a.x, b.y - a.y);
    if (length < EPS) continue;
    // The interior side's unit normal; the kept half-plane lies `distance` inside the edge line.
    const nx = (inward * -(b.y - a.y)) / length, ny = (inward * (b.x - a.x)) / length;
    const depth = (p: Point): number => (p.x - a.x) * nx + (p.y - a.y) * ny - distance;
    const clipped: Point[] = [];
    for (let j = 0; j < out.length; j++) {
      const p = out[j], q = out[(j + 1) % out.length];
      const dp = depth(p), dq = depth(q);
      if (dp >= 0) clipped.push(p);
      if (dp >= 0 !== dq >= 0) {
        const t = dp / (dp - dq);
        clipped.push({ x: p.x + (q.x - p.x) * t, y: p.y + (q.y - p.y) * t });
      }
    }
    out = clipped.filter((p, j) => !samePoint(p, clipped[(j + 1) % clipped.length]));
  }
  return out.length >= 3 && Math.abs(ringSignedArea(out)) > EPS ? out : [];
}
